import type { Editor } from '@tiptap/core';
import { EditorContent, useEditor } from '@tiptap/react';
import Button from 'devextreme-react/button';
import { useCallback, useEffect, useRef } from 'react';
import { createMarkdownEditorExtensions } from '../markdownEditor/markdownEditorExtensions';
import { showErrorToast } from '../notifications/toast';
import { tipAssetMarkdownToDisplay, tipAssetMarkdownToPersist } from './tipAssetMarkdown';
import './tipBodyEditor.css';

type MarkdownStorage = {
  markdown: {
    getMarkdown: () => string;
  };
};

function readLiveMarkdown(editor: { storage: unknown } | null): string {
  if (!editor) return '';
  try {
    return (editor.storage as MarkdownStorage).markdown.getMarkdown();
  } catch {
    return '';
  }
}

export interface TipBodyEditorProps {
  value: string;
  onChange: (markdown: string) => void;
  assetUrl: (id: string) => string;
  uploadAsset: (file: File) => Promise<{ id: string }>;
  disabled?: boolean;
}

export function TipBodyEditor({
  value,
  onChange,
  assetUrl,
  uploadAsset,
  disabled = false
}: TipBodyEditorProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const assetUrlRef = useRef(assetUrl);
  const uploadAssetRef = useRef(uploadAsset);
  assetUrlRef.current = assetUrl;
  uploadAssetRef.current = uploadAsset;

  const editorRef = useRef<Editor | null>(null);
  const insertImageFile = useCallback(async (file: File, target: Editor) => {
    try {
      const uploaded = await uploadAssetRef.current(file);
      target.chain().focus().setImage({ src: assetUrlRef.current(uploaded.id), alt: file.name }).run();
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Image upload failed');
    }
  }, []);

  const editor = useEditor({
    extensions: createMarkdownEditorExtensions({ placeholder: 'Write the tip…' }),
    content: tipAssetMarkdownToDisplay(value, assetUrl),
    editable: !disabled,
    editorProps: {
      handlePaste(_view, event) {
        const image = Array.from(event.clipboardData?.files ?? []).find((file) =>
          file.type.startsWith('image/')
        );
        const current = editorRef.current;
        if (!image || !current) return false;
        event.preventDefault();
        void insertImageFile(image, current);
        return true;
      },
      handleDrop(_view, event) {
        const image = Array.from(event.dataTransfer?.files ?? []).find((file) =>
          file.type.startsWith('image/')
        );
        const current = editorRef.current;
        if (!image || !current) return false;
        event.preventDefault();
        void insertImageFile(image, current);
        return true;
      }
    },
    onCreate: ({ editor: created }) => {
      editorRef.current = created;
    },
    onUpdate: ({ editor: next }) => {
      editorRef.current = next;
      onChange(tipAssetMarkdownToPersist(readLiveMarkdown(next)));
    }
  });
  editorRef.current = editor;

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  useEffect(() => {
    if (!editor) return;
    const current = tipAssetMarkdownToPersist(readLiveMarkdown(editor));
    if (current.trim() === (value ?? '').trim()) return;
    editor.commands.setContent(tipAssetMarkdownToDisplay(value, assetUrl));
  }, [assetUrl, editor, value]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL', previous ?? 'https://');
    if (url === null) return;
    if (!url.trim()) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
  }, [editor]);

  return (
    <div className="tip-body-editor">
      <div className="tip-body-editor__toolbar" role="toolbar" aria-label="Tip formatting">
        <Button
          icon="bold"
          stylingMode="text"
          hint="Bold"
          disabled={disabled}
          onClick={() => editor?.chain().focus().toggleBold().run()}
        />
        <Button
          icon="italic"
          stylingMode="text"
          hint="Italic"
          disabled={disabled}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
        />
        <Button
          text="H2"
          stylingMode="text"
          hint="Heading 2"
          disabled={disabled}
          onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
        />
        <Button
          text="H3"
          stylingMode="text"
          hint="Heading 3"
          disabled={disabled}
          onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
        />
        <span className="tip-body-editor__sep" aria-hidden />
        <Button
          icon="bulletlist"
          stylingMode="text"
          hint="Bullet list"
          disabled={disabled}
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
        />
        <Button
          icon="orderedlist"
          stylingMode="text"
          hint="Numbered list"
          disabled={disabled}
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
        />
        <Button
          icon="link"
          stylingMode="text"
          hint="Link"
          disabled={disabled}
          onClick={setLink}
        />
        <Button
          icon="textdocument"
          stylingMode="text"
          hint="Insert table"
          disabled={disabled}
          onClick={() => editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
        />
        <Button
          icon="image"
          stylingMode="text"
          hint="Insert image"
          disabled={disabled}
          onClick={() => fileRef.current?.click()}
        />
      </div>
      <div className="tip-body-editor__stage">
        <EditorContent editor={editor} />
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file && editor) void insertImageFile(file, editor);
        }}
      />
    </div>
  );
}
